import { useGetDailyPnlQuery } from '../../../../../apis/statistics/statistics-api.api.ts'
import { DashboardChartBox } from '../dashboard-chart-box.tsx'
import ReactECharts, { EChartsOption } from 'echarts-for-react'
import { TopLevelFormatterParams } from 'echarts/types/dist/shared'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectDashboardInput } from '../../../../../redux/slices/dashboard-input.slice.ts'
import dayjs from 'dayjs'
import {
  selectTheme,
  selectThemeStyle,
} from '../../../../../features/setting/setting.slice.ts'
import {
  currencyValueFormatter,
  getAxisUnitScale,
} from '../../../../../utils/formatters.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { useMemo, useRef, useState } from 'react'
import { getAlignedDualAxisScaleConfig } from '../../../../../common/echarts/dual-axis-scale.util.ts'
import { getEchartsTheme } from '../../../../../utils/helpers.ts'
import { useFixedBreakpoint } from '../../../../../common/hooks/use-is-mobile.ts'

interface CumulativePnlChartProps {
  bookId: string
}

type CumulativePnlLegendSelected = {
  'Cumulative PnL': boolean
  Drawdown: boolean
}

export const CumulativePnlChart: React.FC<CumulativePnlChartProps> = ({
  bookId,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const colorTheme = useAppSelector(selectTheme)
  const echartsTheme = getEchartsTheme(colorTheme)
  const dashboardInput = useAppSelector(selectDashboardInput)
  const [legendSelected, setLegendSelected] =
    useState<CumulativePnlLegendSelected>({
      'Cumulative PnL': true,
      Drawdown: true,
    })

  const screens = useFixedBreakpoint()

  const { data } = useGetDailyPnlQuery({
    bookId: bookId,
    startDate: dayjs(dashboardInput.dateRange[0]).format('YYYY-MM-DD'),
    endDate: dayjs(dashboardInput.dateRange[1]).format('YYYY-MM-DD'),
  })

  // echarts-for-react deep-compares `onEvents` and recreates the chart when it
  // differs, so keep the handler identity stable and read the latest logic from a ref.
  const legendSelectChangedRef = useRef<
    (params: { selected: CumulativePnlLegendSelected }) => void
  >(() => {})
  legendSelectChangedRef.current = (params: {
    selected: CumulativePnlLegendSelected
  }) => {
    setLegendSelected((prev) => ({
      ...prev,
      ...params.selected,
    }))
  }

  const onEvents = useMemo(
    () => ({
      legendselectchanged: (params: {
        selected: CumulativePnlLegendSelected
      }) => legendSelectChangedRef.current(params),
    }),
    []
  )

  if (!data) return <></>

  // Ensure data has at least 20 entries
  const requiredLength = 20
  const missingLength = requiredLength - data.data.personalDailyPnls.length

  // Create a new array based on the original data
  const extendedData = [...data.data.personalDailyPnls]

  if (missingLength > 0) {
    const firstDate =
      data.data.personalDailyPnls.length > 0
        ? new Date(data.data.personalDailyPnls[0].date)
        : new Date()
    for (let i = 1; i <= missingLength; i++) {
      const newDate = new Date(firstDate)
      newDate.setDate(firstDate.getDate() - i)
      extendedData.unshift({
        date: newDate.toISOString().split('T')[0], // Format date as YYYY-MM-DD
        cumulativePnl: 0,
        pnl: 0,
        returnOnBP: 0,
        returnOnGMV: 0,
        drawdown: 0,
      })
    }
  }

  const labelCount = screens.lg ? 30 : screens.md ? 20 : screens.sm ? 15 : 10
  const interval = Math.max(1, Math.floor(extendedData.length / labelCount))

  const {
    primaryAxis: pnlAxisScale,
    secondaryAxis: drawdownAxisScale,
    secondaryAxisVisible,
  } = getAlignedDualAxisScaleConfig(
    extendedData.map((d) => d.cumulativePnl),
    [
      {
        values: extendedData.map((d) => d.drawdown),
        enabled: legendSelected.Drawdown,
      },
    ]
  )

  // Each axis picks its own magnitude unit (K/M/B/T) so drawdown labels are not
  // flattened by the usually larger cumulative PnL scale.
  const pnlAxisUnit = getAxisUnitScale([pnlAxisScale.min, pnlAxisScale.max])
  const drawdownAxisUnit = getAxisUnitScale([
    drawdownAxisScale.min,
    drawdownAxisScale.max,
  ])

  const option: EChartsOption = {
    grid: {
      left: 40,
      right: 40,
      bottom: 115,
    },
    xAxis: {
      type: 'category',
      data: extendedData.map((d) => d.date),
      axisLabel: {
        color: themeStyle.sumText,
        rotate: 60,
        interval,
        formatter: (value: string, index: number) =>
          index % 2 === 0 ? value : '',
      },
    },
    yAxis: [
      {
        type: 'value',
        name: `Cumulative PnL ($${pnlAxisUnit.suffix})`,
        min: pnlAxisScale.min,
        max: pnlAxisScale.max,
        interval: pnlAxisScale.interval,
        splitNumber: pnlAxisScale.splitNumber,
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
          formatter: pnlAxisUnit.labelFormatter,
        },
      },
      {
        type: 'value',
        name: `Drawdown ($${drawdownAxisUnit.suffix})`,
        show: secondaryAxisVisible,
        position: 'right',
        min: drawdownAxisScale.min,
        max: drawdownAxisScale.max,
        interval: drawdownAxisScale.interval,
        splitNumber: drawdownAxisScale.splitNumber,
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
          formatter: drawdownAxisUnit.labelFormatter,
        },
      },
    ],
    legend: {
      data: ['Cumulative PnL', 'Drawdown'],
      selected: legendSelected,
      top: 'bottom',
      textStyle: {
        color: themeStyle.sumText,
      },
    },
    tooltip: {
      trigger: 'axis',
      extraCssText: 'text-align: left;',
      formatter: (
        params: TopLevelFormatterParams | TopLevelFormatterParams[]
      ) => {
        const paramArr = Array.isArray(params) ? params : [params]
        const axisValue =
          (paramArr[0] as { axisValue?: string; axisValueLabel?: string })
            .axisValueLabel ??
          (paramArr[0] as { axisValue?: string; axisValueLabel?: string })
            .axisValue ??
          (paramArr[0] as { value?: string | number }).value ??
          ''
        let tooltipText = `${axisValue}<br/>`
        paramArr.forEach((param) => {
          const value = (param as { value?: string | number }).value
          const marker = (param as { marker?: string }).marker || ''
          const seriesName = (param as { seriesName?: string }).seriesName || ''

          tooltipText += `${marker} ${seriesName}: ${currencyValueFormatter(
            {
              value: value,
            } as ValueFormatterParams,
            { decimal: 0, currency: 'USD' }
          )}<br/>`
        })
        return tooltipText
      },
    },
    series: [
      {
        type: 'line',
        name: 'Cumulative PnL',
        data: extendedData.map((d) => d.cumulativePnl.toFixed(0)),
      },
      {
        type: 'bar',
        name: 'Drawdown',
        yAxisIndex: 1,
        data: extendedData.map((d) => d.drawdown.toFixed(0)),
        itemStyle: {
          color: '#f26466',
        },
      },
    ],
  }

  return (
    <DashboardChartBox title={t('dashboard.cumulative_pnl')}>
      <ReactECharts
        option={option}
        style={{
          height: '400px',
        }}
        theme={echartsTheme.themeName}
        onEvents={onEvents}
      />
    </DashboardChartBox>
  )
}
